import { Backdrop, Box, Button, CircularProgress, SelectChangeEvent, Theme, Typography, useTheme } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { RootStateType } from "../../../redux-store/reducers";
import { isModuleAllowed } from "../../../utils/utilityFunctions";
import BreadCrum from "../../../lib/BreadCrum";
import { Form, Formik, FormikProps } from "formik";
import { useRef, useState } from "react";
import { ExcludedSecurityObj, FamilyMembersFeeListDataObj, FamilyObj, GetExcludedSecuritiesResponseBody, GetFamilyMembersFeeListDataResponseBody, GetReportFamiliesListResponseBody, SecurityPreviewObj, UpdateExcludedSecurityListRequestBody, UpdateExcludedSecurityListResponseBody } from "../../../redux-store/types/onBoarding";
import * as Yup from 'yup';
import { getFiscalYearOptions, getQuartersForFiscalYear } from "../financialYear";
import MFSelectField from "../../../lib/formik/SelectField";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { getExcludedSecurities, getFamilyMembersFeeListData, getReportFamiliesList, updateExcludedSecurityList } from "../../../redux-store/actions/reports";
import { showError } from "../../../redux-store/actions/auth";
import { MFSubmitButton } from "../../../lib/formik";
import NoData from "../NoData";
import { PaginationObj } from "../../investorOverview/types";
import SecurityExclusionTable from "./SecurityExclusionTable";
import IncludedSecuritiesPopup from "./IncludedSecuritiesPopup";
import AddSecuritiesExclusionsPopup from "./AddSecuritiesExclusionsPopup.tsx";
import { ToggleBox } from "../../../assets/SvgIcons";
import StatusPopup from "../StatusPopup";

interface ModifiedFamilyObj extends FamilyObj {
    label: string;
    value: string;
}

interface ModifiedFamilyMembersFeeListDataObj extends FamilyMembersFeeListDataObj {
    label: string;
    value: string;
}

export interface FilterValues {
    year: string;
    quarter: string;
    family: ModifiedFamilyObj | null;
    individualArr: Array<ModifiedFamilyMembersFeeListDataObj>;
    individual: ModifiedFamilyMembersFeeListDataObj | null;
}

const initialFilterValues: FilterValues = {
    year: "",
    quarter: "",
    family: null,
    individualArr: [],
    individual: null
}

const filterValidationSchema = Yup.object().shape({
    year: Yup
        .string()
        .required('Please select Year'),
    quarter: Yup
        .string()
        .required('Please select Quarter'),
    family: Yup
        .object()
        .nullable()
        .test(
            "family-required",
            "Please select Family",
            function (value) { return !!value }
        ),
    individual: Yup
        .object()
        .nullable()
        .test(
            "individual-required",
            "Please select Individual",
            function (value) { return !!value }
        ),
})

const YEARS = getFiscalYearOptions()

const FILTER_FIELD_HEIGHT = "56px";
const FILTER_ERROR_GAP = "6px";
const FILTER_ERROR_SLOT_HEIGHT = "22px";
const FILTER_COLUMN_HEIGHT = `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP} + ${FILTER_ERROR_SLOT_HEIGHT})`;

const filterInputHeightSx = {
    "& .MuiInputBase-root": {
        height: FILTER_FIELD_HEIGHT,
        minHeight: FILTER_FIELD_HEIGHT,
        maxHeight: FILTER_FIELD_HEIGHT,
        boxSizing: "border-box",
    },
    "& .MuiSelect-select": {
        padding: "15px",
        height: "100%",
        minHeight: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
        height: FILTER_FIELD_HEIGHT,
        minHeight: FILTER_FIELD_HEIGHT,
        maxHeight: FILTER_FIELD_HEIGHT,
        padding: "0 9px",
        alignItems: "center",
        boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-input": {
        padding: "15px 6px",
    },
};

const filterErrorSx = {
    position: "absolute",
    left: 0,
    top: `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP})`,
    margin: 0,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
};

const filterFieldWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 1 auto" },
    minWidth: { xs: "100%", lg: "145px" },
    maxWidth: { xs: "100%", lg: "205px" },
    width: { xs: "100%", lg: "auto" },
    position: "relative",
    minHeight: FILTER_COLUMN_HEIGHT,
    "& .MuiFormControl-root": {
        my: 0,
        paddingBottom: 0,
    },
    "& .MuiFormHelperText-root": filterErrorSx,
    ...filterInputHeightSx,
};

const filterQuarterWrapperSx = {
    ...filterFieldWrapperSx,
    minWidth: { xs: "100%", lg: "240px" },
    maxWidth: { xs: "100%", lg: "280px" },
};

const filterSearchDropdownWrapperSx = {
    ...filterFieldWrapperSx,
    minWidth: { xs: "100%", lg: "300px" },
    maxWidth: { xs: "100%", lg: "350px" },
};

const filterSubmitWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 0 auto" },
    position: "relative",
    minHeight: { xs: "auto", lg: FILTER_COLUMN_HEIGHT },
    display: "flex",
    alignItems: "flex-start",
    width: { xs: "100%", lg: "auto" },
    marginTop: { xs: "1rem", lg: 0 }
};

const filterSubmitButtonSx = (theme: Theme) => ({
    ...theme.typography.body_500,
    fontWeight: 600,
    height: { xs: "auto", lg: FILTER_FIELD_HEIGHT },
    minHeight: { xs: "42px", lg: FILTER_FIELD_HEIGHT },
    maxHeight: { xs: "48px", lg: FILTER_FIELD_HEIGHT },
    py: { xs: "6px", lg: 0 },
    px: { xs: "16px", lg: "20px" },
    lineHeight: 1.2,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    width: { xs: "100%", lg: "auto" },
    minWidth: { xs: "unset", lg: "fit-content" },
    flexShrink: 0,
    "& .MuiCircularProgress-root": {
        color: theme.palette.text.primary,
    },
});

export default function SecurityExclusionMgmt(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);

    // State
    const [loadingStatus, setLoadingStatus] = useState<
        "individualDataLoading" |
        "tableDataLoading" |
        "appliedFilterLoading" |
        ""
    >("")
    const [familiesList, setFamiliesList] = useState<Array<ModifiedFamilyObj>>([])
    const [areFiltersApplied, setAreFiltersApplied] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ExcludedSecurityObj & { isExcluded: boolean; }>>([])
    const [includedSecuritiesTableData, setIncludedSecuritiesTableData] = useState<Array<ExcludedSecurityObj & { isExcluded: boolean; }>>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0,
    })
    const [isIncludedSecuritiesPopupOpen, setIsIncludedSecuritiesPopupOpen] = useState<boolean>(false)
    const [isAddSecuritiesExclusionsPopupOpen, setIsAddSecuritiesExclusionsPopupOpen] = useState<boolean>(false)
    const [updateStatusPopupData, setUpdateStatusPopupData] = useState<UpdateExcludedSecurityListResponseBody & { isOpen: boolean }>({
        isOpen: false,
        isSuccess: false,
        message: "",
        body: ""
    })

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'Security Exclusion Management',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        }
    ] : [])

    const filterFormikRef = useRef<FormikProps<FilterValues>>(null);
    const lastAppliedFilterValuesRef = useRef<FilterValues | null>(null);

    const handleViewAuditsClick = () => {
        history.push(`${pathName}/audit-history`)
    }

    const handleClearIncludedSecurities = () => {
        setIncludedSecuritiesTableData([])
        setTableData(prevData =>
            prevData.map(obj => ({ ...obj, isExcluded: true }))
        )
    }

    const handleOpenIncludedSecuritiesPopup = () => {
        setIsIncludedSecuritiesPopupOpen(true)
    }

    const handleCloseIncludedSecuritiesPopup = () => {
        setIsIncludedSecuritiesPopupOpen(false)
    }

    const handleOpenAddSecuritiesExclusionsPopup = () => {
        setIsAddSecuritiesExclusionsPopupOpen(true)
    }

    const handleCloseAddSecuritiesExclusionsPopup = () => {
        setIsAddSecuritiesExclusionsPopupOpen(false)
    }

    const handleYearChange = (event: SelectChangeEvent<unknown>) => {
        if (!filterFormikRef?.current) {
            return
        }

        const { values, setValues } = filterFormikRef.current

        setValues({
            ...values,
            year: event.target.value as string,
            quarter: "",
            family: null,
            individualArr: [],
            individual: null
        })
    }

    const handleQuarterChange = (event: SelectChangeEvent<unknown>) => {
        if (!filterFormikRef?.current) {
            return
        }

        const { values, setValues } = filterFormikRef.current

        setValues({
            ...values,
            quarter: event.target.value as string,
            family: null,
            individualArr: [],
            individual: null
        })
    }

    const fetchFamiliesData = async (searchInput: string) => {
        try {
            if (`${searchInput ?? ""}`.trim().length !== 0) {

                const payload = {
                    rmId: `${iwappRmId}`,
                    searchInput: `${searchInput ?? ""}`.trim()
                }
                const response = await (dispatch(getReportFamiliesList(payload)) as unknown as Promise<GetReportFamiliesListResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message || "Data not found"))
                    setFamiliesList([])

                    return
                }

                if (response?.isSuccess) {
                    const modifiedFamiliesList = (response?.families ?? [])
                        .filter((obj: FamilyObj) => `${obj?.family_name ?? ""}`.toLowerCase() !== "all")
                        .map((obj: FamilyObj) => ({
                            ...obj,
                            label: `${obj?.family_name ?? ""} ${obj?.family_id ? `(${obj.family_id})` : ''}`,
                            value: obj?.family_id
                        }))

                    setFamiliesList(modifiedFamiliesList)
                }
            } else {
                setFamiliesList([])
            }
        } catch (error) {
            console.log("Error: ", error)
        }
    }

    const handleFamilyChange = async (selectedObj: Record<string, any>) => {
        try {
            if (!filterFormikRef?.current) {
                return
            }

            const { values, setValues } = filterFormikRef.current

            if (!selectedObj) {
                setValues({
                    ...values,
                    family: null,
                    individualArr: [],
                    individual: null
                })

                return
            }

            setValues({
                ...values,
                family: selectedObj as ModifiedFamilyObj,
                individualArr: [],
                individual: null
            })

            setLoadingStatus("individualDataLoading")

            const payload = {
                year: Number(values.year),
                quarter: values.quarter,
                familyId: selectedObj?.family_id ?? ""
            }

            const response = await (dispatch(getFamilyMembersFeeListData(payload)) as unknown as Promise<GetFamilyMembersFeeListDataResponseBody>)

            if (!response) {
                return
            }

            if (!response?.isSuccess) {
                dispatch(showError(response?.message || "Data not found"))
                return
            }

            if (response.isSuccess) {
                const modifiedIndividualArr = (response?.data?.members ?? []).map((obj: FamilyMembersFeeListDataObj) => ({
                    ...obj,
                    label: `${obj?.name ?? ""} ${obj?.clientCode ? "(" + obj?.clientCode + ")" : ""} ${obj?.investorType ? "(" + obj?.investorType + ")" : ""} ${`${obj?.isHof ?? ""}`.toLowerCase() === "y" ? "(Head Of Family)" : ""}`,
                    value: obj?.clientCode,
                }))

                setValues({
                    ...values,
                    family: selectedObj as ModifiedFamilyObj,
                    individualArr: modifiedIndividualArr ?? [],
                    individual: null
                })
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const fetchExcludedSecurities = async ({
        selectedYear,
        selectedQuarter,
        selectedPortfolioIdsArr,
        selectedCurrentPage,
        selectedRowsPerPage,
        currentIncludedSecurityIds = (includedSecuritiesTableData ?? []).map(item => item.security)
    }: {
        selectedYear: string;
        selectedQuarter: string;
        selectedPortfolioIdsArr: Array<string>;
        selectedCurrentPage: number;
        selectedRowsPerPage: number;
        currentIncludedSecurityIds?: Array<string>;
    }): Promise<boolean> => {

        const payload = {
            year: Number(selectedYear ?? ""),
            quarter: selectedQuarter,
            portfolioIds: selectedPortfolioIdsArr ?? [],
            currentPage: selectedCurrentPage,
            rowsPerPage: selectedRowsPerPage
        }

        const response = await (dispatch(getExcludedSecurities(payload)) as unknown as Promise<GetExcludedSecuritiesResponseBody>)

        if (!response) {
            setTableData([])
            setTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            });

            return true
        }

        if (!response?.isSuccess) {
            dispatch(showError(response?.message || "Data not found"))

            setTableData([])
            setTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            });

            return true
        }

        if (response.isSuccess) {

            const modifiedResponseData = (response?.data ?? []).map((obj: ExcludedSecurityObj) => ({
                ...obj,
                isExcluded: !currentIncludedSecurityIds.includes(obj.security)
            }))

            setTableData(modifiedResponseData ?? [])
            setTablePaginationObj(prevState => ({
                ...prevState,
                currentPage: selectedCurrentPage - 1,
                rowsPerPage: selectedRowsPerPage,
                totalRows: response?.count ?? 0
            }))
        }

        return true
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("tableDataLoading")

            await fetchExcludedSecurities({
                selectedYear: lastAppliedFilterValuesRef.current?.year ?? "",
                selectedQuarter: lastAppliedFilterValuesRef.current?.quarter ?? "",
                selectedPortfolioIdsArr: [lastAppliedFilterValuesRef.current?.individual?.clientCode ?? ""],
                selectedCurrentPage: 1,
                selectedRowsPerPage: Number(event.target.value)
            })
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleExclusionChange = (securityId: string, checked: boolean) => {
        setTableData(prevData =>
            prevData.map(obj =>
                (obj.security === securityId) ? { ...obj, isExcluded: checked } : obj
            )
        )

        setIncludedSecuritiesTableData(prevIncluded => {
            if (!checked) { // isExcluded = false
                const alreadyIncluded = prevIncluded.some(obj => obj.security === securityId)
                if (alreadyIncluded) { // if security is already in includedSecuritiesTableData, then return prevIncluded
                    return prevIncluded
                }

                const newSecurityEntry = tableData.find(obj => obj.security === securityId) // find new security in tableData to add in includedSecuritiesTableData

                if (newSecurityEntry) {
                    return [...prevIncluded, { ...newSecurityEntry, isExcluded: false }]
                }

                return prevIncluded
            } else {
                return prevIncluded.filter(obj => obj.security !== securityId) //remove the security whose isExcluded = true
            }
        })
    }

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("tableDataLoading")

            await fetchExcludedSecurities({
                selectedYear: lastAppliedFilterValuesRef.current?.year ?? "",
                selectedQuarter: lastAppliedFilterValuesRef.current?.quarter ?? "",
                selectedPortfolioIdsArr: [lastAppliedFilterValuesRef.current?.individual?.clientCode ?? ""],
                selectedCurrentPage: page + 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage
            })
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleApplyFilters = async (values: FilterValues) => {
        try {
            setIncludedSecuritiesTableData([])

            setLoadingStatus("appliedFilterLoading")

            const isSuccess = await fetchExcludedSecurities({
                selectedYear: values?.year ?? "",
                selectedQuarter: values?.quarter ?? "",
                selectedPortfolioIdsArr: [values?.individual?.clientCode ?? ""],
                selectedCurrentPage: 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                currentIncludedSecurityIds: [],
            })

            if (isSuccess) {
                lastAppliedFilterValuesRef.current = { ...values }

                setAreFiltersApplied(true)
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleSaveClick = async () => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("tableDataLoading")

            const appliedValues = lastAppliedFilterValuesRef.current

            const payload = {
                year: Number(appliedValues.year),
                quarter: appliedValues.quarter,
                securityList: (includedSecuritiesTableData ?? []).map(obj => ({
                    portfolio: obj?.portfolio ?? "",
                    name: obj?.portfolioName ?? "",
                    security: obj?.security ?? "",
                    securityName: obj?.securityName ?? "",
                    isExcluded: "N",
                    exclusionReason: "",
                    familyId: obj?.familyId ?? "",
                    familyName: obj?.familyName ?? "",
                    clientCode: obj?.clientCode ?? ""
                }))
            }

            const response = await (dispatch(updateExcludedSecurityList(payload)) as unknown as Promise<UpdateExcludedSecurityListResponseBody>);

            if (response) {
                setUpdateStatusPopupData({
                    isOpen: true,
                    isSuccess: response?.isSuccess,
                    message: response?.message ?? "",
                    body: response?.body ?? ""
                })

                if (response?.isSuccess) {
                    setIncludedSecuritiesTableData([])

                    await fetchExcludedSecurities({
                        selectedYear: appliedValues?.year ?? "",
                        selectedQuarter: appliedValues?.quarter ?? "",
                        selectedPortfolioIdsArr: [appliedValues?.individual?.clientCode ?? ""],
                        selectedCurrentPage: 1,
                        selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                        currentIncludedSecurityIds: []
                    })
                }
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const triggerExcludedSecuritiesApi = async () => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("tableDataLoading")

            const appliedValues = lastAppliedFilterValuesRef.current

            setIncludedSecuritiesTableData([])

            await fetchExcludedSecurities({
                selectedYear: appliedValues?.year ?? "",
                selectedQuarter: appliedValues?.quarter ?? "",
                selectedPortfolioIdsArr: [appliedValues?.individual?.clientCode ?? ""],
                selectedCurrentPage: 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                currentIncludedSecurityIds: []
            })
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleUpdateStatusPopupClose = () => {
        setUpdateStatusPopupData((prev) => ({
            ...prev,
            isOpen: false,
        }))
    }

    return (
        <>
            <Backdrop
                sx={(theme: Theme) => ({
                    color: theme.palette.common.white,
                    zIndex: theme.zIndex.modal + 1
                })}
                open={["individualDataLoading"].includes(loadingStatus)}
            >
                <CircularProgress sx={(theme: Theme) => ({ color: theme.palette.primary[500] })} />
            </Backdrop>
            <Box sx={{ marginBottom: "0.5rem" }}>
                <BreadCrum array={routerPath} />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: { xs: "flex-start", md: "space-between" },
                    alignItems: { xs: "stretch", md: "center" },
                    rowGap: "2rem",
                    marginBottom: "2rem"
                }}
            >
                <Typography
                    variant="h1"
                    sx={{ color: theme.palette.text.primary }}
                >
                    Security Exclusion Management
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleViewAuditsClick}
                    sx={{
                        ...theme.typography.body_600,
                        color: theme.palette.primary[500],
                        borderColor: theme.palette.primary[500],
                        borderWidth: "1.5px",
                        borderRadius: "10px",
                        width: { xs: "100%", md: "auto" },
                        py: { xs: "0.3rem", sm: "0.4rem" },
                        px: { xs: "1rem", sm: "1.5rem", md: "2rem" },
                        "&:hover": {
                            backgroundColor: `${theme.palette.primary[500]}1A`,
                            borderWidth: "1.5px",
                        },
                    }}
                >
                    View Audits
                </Button>
            </Box>
            <Box
                sx={{
                    width: { xs: "100%", lg: "auto" },
                    minWidth: 0,
                    maxWidth: "100%"
                }}
            >
                <Formik
                    innerRef={filterFormikRef}
                    validationSchema={filterValidationSchema}
                    initialValues={initialFilterValues}
                    onSubmit={handleApplyFilters}
                >
                    {({ values }) => {
                        const quarterOptions = getQuartersForFiscalYear(values?.year ?? "")

                        return (
                            <Form style={{ width: "100%", maxWidth: "100%" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", lg: "row" },
                                        flexWrap: { xs: "nowrap", lg: "wrap" },
                                        alignItems: { xs: "stretch", lg: "flex-start" },
                                        justifyContent: 'flex-start',
                                        gap: { xs: "0.8rem", lg: "1rem" },
                                        width: "100%",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                    }}
                                >
                                    <Box sx={filterFieldWrapperSx}>
                                        <MFSelectField
                                            name='year'
                                            label="Year *"
                                            items={YEARS}
                                            onChange={handleYearChange}
                                            disabled={["appliedFilterLoading", "tableDataLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterQuarterWrapperSx}>
                                        <MFSelectField
                                            name='quarter'
                                            label="Quarter *"
                                            items={quarterOptions}
                                            onChange={handleQuarterChange}
                                            disabled={["appliedFilterLoading", "tableDataLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterSearchDropdownWrapperSx}>
                                        <FormikSearchDropdown
                                            name="family"
                                            label="Family *"
                                            options={familiesList ?? []}
                                            fetchData={fetchFamiliesData}
                                            filterOptions={(x) => x}
                                            onOpen={() => {
                                                if (!filterFormikRef?.current?.values?.family) {
                                                    setFamiliesList([]);
                                                }
                                            }}
                                            onChange={handleFamilyChange}
                                            disabled={["appliedFilterLoading", "tableDataLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterSearchDropdownWrapperSx}>
                                        <FormikSearchDropdown
                                            name="individual"
                                            label="Individual *"
                                            options={values?.individualArr ?? []}
                                            disabled={["appliedFilterLoading", "tableDataLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterSubmitWrapperSx}>
                                        <MFSubmitButton
                                            label="Apply Filters"
                                            fullWidth={false}
                                            sx={filterSubmitButtonSx(theme)}
                                            loadingEnable={["appliedFilterLoading"].includes(loadingStatus)}
                                            disabled={["tableDataLoading"].includes(loadingStatus)}
                                            loadingPosition="center"
                                        />
                                    </Box>
                                </Box>
                            </Form>
                        )
                    }}
                </Formik>
            </Box>
            {areFiltersApplied && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: { xs: "3rem", lg: 0 },
                        marginBottom: { xs: "0.5rem", lg: 0 }
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={{ color: theme.palette.text.primary }}
                    >
                        Securities List
                    </Typography>
                    <Button
                        variant="text"
                        sx={{
                            ...theme.typography.body_600,
                            color: theme.palette.primary[500],
                            marginRight: "1.5rem"
                        }}
                        endIcon={<AddCircleIcon sx={{ color: theme.palette.primary[500] }} />}
                        onClick={handleOpenAddSecuritiesExclusionsPopup}
                        disabled={["appliedFilterLoading", "tableDataLoading"].includes(loadingStatus)}
                    >
                        Add
                    </Button>
                </Box>
            )}
            <Box
                sx={{
                    padding: '25px',
                    borderRadius: '15px',
                    background: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginTop: !areFiltersApplied ? "1rem" : 0
                }}
            >
                {!areFiltersApplied && <NoData />}
                {areFiltersApplied && (
                    <>
                        {(
                            !["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus) &&
                            (includedSecuritiesTableData ?? []).length > 0
                        ) && (
                                <>
                                    <Box
                                        sx={(theme: Theme) => ({
                                            padding: { xs: "10px" },
                                            backgroundColor: theme.palette.grey[900],
                                            display: "flex",
                                            flexWrap: "wrap",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mt: "10px"
                                        })}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                columnGap: "10px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "flex",
                                                    cursor: "pointer"
                                                }}
                                                onClick={handleClearIncludedSecurities}
                                            >
                                                <ToggleBox />
                                            </Box>
                                            <Typography
                                                variant="overline_600"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.primary[500],
                                                    lineHeight: "17.5px"
                                                })}
                                            >
                                                {(includedSecuritiesTableData ?? []).length} {(includedSecuritiesTableData ?? []).length === 1 ? "Security" : "Securities"} Included
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="overline_600"
                                            sx={(theme: Theme) => ({
                                                color: theme.palette.primary[500],
                                                cursor: "pointer",
                                                lineHeight: "17.5px",
                                                textAlign: "right"
                                            })}
                                            onClick={handleOpenIncludedSecuritiesPopup}
                                        >
                                            View Included Securities
                                        </Typography>
                                    </Box>

                                </>
                            )}
                        <SecurityExclusionTable
                            isLoading={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                            tableData={tableData}
                            tablePaginationObj={tablePaginationObj}
                            handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                            handleTablePageChange={handleTablePageChange}
                            onExclusionChange={handleExclusionChange}
                        />
                        {(!["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus) && tablePaginationObj.totalRows !== 0) && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    marginTop: "1.5rem",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    sx={{
                                        ...theme.typography.body_600,
                                        width: "fit-content",
                                        borderRadius: "10px",
                                        px: "2rem",
                                        py: "0.5rem",
                                    }}
                                    onClick={() => handleSaveClick()}
                                >
                                    Save
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <IncludedSecuritiesPopup
                open={isIncludedSecuritiesPopupOpen}
                onClose={handleCloseIncludedSecuritiesPopup}
                includedSecuritiesTableData={includedSecuritiesTableData}
            />
            <AddSecuritiesExclusionsPopup
                open={isAddSecuritiesExclusionsPopupOpen}
                onClose={handleCloseAddSecuritiesExclusionsPopup}
                lastAppliedFilterValuesRef={lastAppliedFilterValuesRef}
                triggerExcludedSecuritiesApi={triggerExcludedSecuritiesApi}
            />
            <StatusPopup
                open={updateStatusPopupData.isOpen}
                isSuccess={updateStatusPopupData.isSuccess}
                message={updateStatusPopupData.message}
                buttonText={updateStatusPopupData.isSuccess ? "Done" : "Cancel"}
                onButtonClick={handleUpdateStatusPopupClose}
            />
        </>
    )
}